import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingTranslations } from '@/constants/meeting.translations';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MeetingRoomProps {
  roomName: string;
  userName: string;
  userRole: string;
  dealTitle?: string;
  recordingEnabled?: boolean;
  onJoin?: () => void;
  onLeave?: (audioBlob?: Blob) => void;
}

declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

const MeetingRoom: React.FC<MeetingRoomProps> = ({
  roomName,
  userName,
  userRole,
  dealTitle,
  recordingEnabled = true,
  onJoin,
  onLeave,
}) => {
  const { language } = useLanguage();
  const t = getMeetingTranslations(language);
  const { toast } = useToast();
  
  const containerRef = useRef<HTMLDivElement>(null);
  const jitsiApiRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(true);
  const [participantCount, setParticipantCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  // Load Jitsi script
  useEffect(() => {
    if (document.querySelector('script[src*="jitsi"]')) return;

    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script only if no other meetings are using it
    };
  }, []);

  // Start audio recording for transcription
  const startAudioRecording = useCallback(async () => {
    if (!recordingEnabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(10000); // Capture every 10 seconds
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      console.log('Audio recording started');
    } catch (error) {
      console.error('Error starting audio recording:', error);
      toast({
        variant: 'destructive',
        title: t.microphoneRequired,
      });
    }
  }, [recordingEnabled, t.microphoneRequired, toast]);

  // Stop audio recording and return blob
  const stopAudioRecording = useCallback((): Promise<Blob | undefined> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(undefined);
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];
        setIsRecording(false);
        console.log('Audio recording stopped, blob size:', audioBlob.size);
        resolve(audioBlob);
      };

      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    });
  }, []);

  // Initialize Jitsi meeting
  const initJitsi = useCallback(() => {
    if (!containerRef.current || !window.JitsiMeetExternalAPI) {
      console.log('Waiting for Jitsi API or container...');
      return;
    }

    setIsLoading(true);

    try {
      const domain = 'meet.jit.si';
      const options = {
        roomName: `AcquizGroup-${roomName}`,
        width: '100%',
        height: 500,
        parentNode: containerRef.current,
        userInfo: {
          displayName: `${userName} (${userRole})`,
        },
        configOverwrite: {
          startWithAudioMuted: true,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          enableWelcomePage: false,
          enableClosePage: false,
          disableInviteFunctions: true,
        },
        interfaceConfigOverwrite: {
          TOOLBAR_BUTTONS: [
            'microphone',
            'camera',
            'chat',
            'raisehand',
            'tileview',
            'fullscreen',
            'settings',
          ],
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: '#1D314D',
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
        },
      };

      const api = new window.JitsiMeetExternalAPI(domain, options);
      jitsiApiRef.current = api;

      // Event handlers
      api.addListener('videoConferenceJoined', () => {
        console.log('Joined video conference');
        setIsConnected(true);
        setIsLoading(false);
        onJoin?.();
        startAudioRecording();
      });

      api.addListener('videoConferenceLeft', async () => {
        console.log('Left video conference');
        setIsConnected(false);
        const audioBlob = await stopAudioRecording();
        onLeave?.(audioBlob);
      });

      api.addListener('participantJoined', () => {
        setParticipantCount(prev => prev + 1);
      });

      api.addListener('participantLeft', () => {
        setParticipantCount(prev => Math.max(0, prev - 1));
      });

      api.addListener('audioMuteStatusChanged', (data: { muted: boolean }) => {
        setIsMuted(data.muted);
      });

      api.addListener('videoMuteStatusChanged', (data: { muted: boolean }) => {
        setIsVideoOff(data.muted);
      });

    } catch (error) {
      console.error('Error initializing Jitsi:', error);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t.errorJoining,
      });
    }
  }, [roomName, userName, userRole, onJoin, onLeave, startAudioRecording, stopAudioRecording, t.errorJoining, toast]);

  // Accept consent and start meeting
  const handleAcceptConsent = useCallback(() => {
    setShowConsentDialog(false);
    
    // Wait for Jitsi script to load
    const checkJitsi = setInterval(() => {
      if (window.JitsiMeetExternalAPI) {
        clearInterval(checkJitsi);
        initJitsi();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => clearInterval(checkJitsi), 10000);
  }, [initJitsi]);

  // Leave meeting
  const handleLeaveMeeting = useCallback(async () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    setIsConnected(false);
    const audioBlob = await stopAudioRecording();
    onLeave?.(audioBlob);
  }, [stopAudioRecording, onLeave]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    jitsiApiRef.current?.executeCommand('toggleAudio');
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    jitsiApiRef.current?.executeCommand('toggleVideo');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <>
      {/* Consent Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t.recordingConsent}
            </DialogTitle>
            <DialogDescription className="pt-4">
              {t.consentMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => onLeave?.()}>
              {t.consentDecline}
            </Button>
            <Button onClick={handleAcceptConsent} className="bg-[#CEAB65] hover:bg-[#CEAB65]/90">
              {t.consentAccept}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Room Card */}
      <Card className="w-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Video className="h-5 w-5" />
              {t.meetingRoom}
            </CardTitle>
            <div className="flex items-center gap-2">
              {isRecording && (
                <Badge variant="destructive" className="animate-pulse">
                  ● REC
                </Badge>
              )}
              <Badge variant="outline" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {participantCount + 1}
              </Badge>
            </div>
          </div>
          {dealTitle && (
            <p className="text-sm text-muted-foreground">
              {t.deal}: {dealTitle}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {/* Jitsi Container */}
          <div 
            ref={containerRef} 
            className="w-full rounded-lg overflow-hidden bg-[#1D314D] min-h-[500px]"
          >
            {isLoading && (
              <div className="flex items-center justify-center h-[500px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
          </div>

          {/* Controls */}
          {isConnected && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button
                variant={isMuted ? "destructive" : "secondary"}
                size="icon"
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              
              <Button
                variant={isVideoOff ? "destructive" : "secondary"}
                size="icon"
                onClick={toggleVideo}
                title={isVideoOff ? "Turn on camera" : "Turn off camera"}
              >
                {isVideoOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
              </Button>

              <Button
                variant="destructive"
                onClick={handleLeaveMeeting}
                className="px-6"
              >
                <PhoneOff className="h-4 w-4 mr-2" />
                {t.leaveMeeting}
              </Button>
            </div>
          )}

          {/* Recording Notice */}
          {recordingEnabled && isConnected && (
            <p className="text-xs text-muted-foreground text-center mt-4 italic">
              {t.consentMessage}
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default MeetingRoom;
